import type { Runners } from '@ps/isp-web-services-types';
import { MarketStatus, RunnerStatusMarketDetails } from '@ps/isp-web-services-types';
import produce from 'immer';
import type { Draft } from 'immer';

import type { RequestError } from '@swf/core/errors/RequestError';
import type { MarketPollingData } from '@swf/sports/types/market';
import updateMarket from '@swf/sports/utils/updateMarket';

import { isDynamicMarketBanner } from '../utils/isDynamicMarketBanner';

import { defaultState } from './defaultState';
import type { Banner } from './types';

type State = typeof defaultState;

export const reducers = {
  clear: (): State => ({ ...defaultState }),
  marketUpdates: (state: State, { data }: MarketPollingData): State =>
    produce(state, (draft) => {
      draft.banners = draft.banners?.reduce<Draft<Banner[]>>((acc, banner) => {
        if (isDynamicMarketBanner(banner)) {
          const marketUpdate = data[banner.market.marketId];

          if (
            !marketUpdate ||
            marketUpdate.marketStatus === MarketStatus.CLOSED ||
            marketUpdate.marketStatus === MarketStatus.INACTIVE
          ) {
            return acc;
          }

          updateMarket(banner.market, marketUpdate);

          const filteredRunners = banner.market.runners.filter(
            (runner) =>
              !(
                typeof runner.winRunnerOdds === 'undefined' ||
                runner.runnerStatus === RunnerStatusMarketDetails.REMOVED
              )
          );

          if (!filteredRunners.length) return acc;

          banner.market.runners = filteredRunners as Draft<Runners>;
        }

        return [...acc, banner];
      }, []);
    }),
  cmsBannerRequested: (state: State): State => ({
    ...state,
    isLoading: true
  }),
  dataLoadSuccess: (_state: State, payload: Banner[]): State => ({
    isLoading: false,
    hasPreloaded: true,
    banners: payload
  }),
  dataLoadFailed: (state: State, payload: RequestError): State => ({
    ...state,
    isLoading: false,
    hasPreloaded: true,
    error: {
      statusCode: payload.headers?.statusCode || 500
    }
  })
};
